import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { playClickSound } from '@/utils/soundUtils';
import { cx } from './cx';
import { focusRing } from './focusRing';
import { getVisiblePages } from './paginationUtils';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** When true (default), hide entirely if totalPages <= 1. */
  hideWhenSinglePage?: boolean;
  /** Optional aria label for the nav. */
  label?: string;
  /** Force compact layout (‹ current / total ›) regardless of viewport. */
  compact?: boolean;
  /** @deprecated Kept for API compat; collapsed pager does not use go-to input. */
  onGoToPage?: (page: number) => void;
}

/** 44×44 touch targets on phones, 36×36 from sm up. Button corners, the secondary Button's fill and edge. */
const btnBase = cx(
  'inline-flex items-center justify-center shrink-0',
  'min-h-11 min-w-11 @bp-sm:min-h-9 @bp-sm:min-w-9',
  'rounded-button border border-btn-secondary-edge bg-btn-secondary text-primary font-ui text-small font-semibold',
  'transition-colors duration-(--k-dur-fast)',
  'disabled:text-disabled disabled:cursor-not-allowed',
  'hover:enabled:bg-action-neutral-hover hover:enabled:border-edge-strong',
  focusRing
);

const btnCurrent = 'border-focus/60 bg-selected text-primary';

/**
 * Collapsed pager — centered, nowrap, max ~7 page tokens.
 * Desktop: Previous | 1 | … | 6 | … | 12 | Next
 * Mobile (<640): ‹ | 3 / 12 | ›
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  hideWhenSinglePage = true,
  label,
  compact = false,
}) => {
  const { t } = useTranslation();

  if (hideWhenSinglePage && totalPages <= 1) return null;

  const safeTotal = Math.max(1, totalPages);
  const safeCurrent = Math.min(Math.max(1, currentPage), safeTotal);
  const pages = getVisiblePages(safeCurrent, safeTotal);
  const prevDisabled = safeCurrent <= 1;
  const nextDisabled = safeCurrent >= safeTotal;

  const go = (page: number) => {
    if (page < 1 || page > safeTotal || page === safeCurrent) return;
    playClickSound();
    onPageChange(page);
  };

  return (
    <nav
      className={cx(
        'flex w-full max-w-full items-center justify-center overflow-hidden',
        className
      )}
      aria-label={label || t('common.pagination', 'Pagination')}
    >
      {/* Compact: ‹ current / total › */}
      <div
        className={cx(
          'items-center justify-center gap-2 flex-nowrap overflow-hidden w-fit max-w-full mx-auto',
          compact ? 'flex' : 'flex @bp-sm:hidden'
        )}
      >
        <button
          type="button"
          className={btnBase}
          disabled={prevDisabled}
          onClick={() => go(safeCurrent - 1)}
          aria-label={t('common.previous', 'Previous')}
        >
          <ChevronLeft className="w-4 h-4" aria-hidden />
        </button>
        <span
          className={cx(
            'font-ui tabular-nums text-primary whitespace-nowrap px-1',
            'text-small'
          )}
          aria-current="page"
        >
          {safeCurrent} / {safeTotal}
        </span>
        <button
          type="button"
          className={btnBase}
          disabled={nextDisabled}
          onClick={() => go(safeCurrent + 1)}
          aria-label={t('common.next', 'Next')}
        >
          <ChevronRight className="w-4 h-4" aria-hidden />
        </button>
      </div>

      {/* Desktop: Prev + collapsed numbers + Next */}
      <div
        className={cx(
          'items-center justify-center gap-1.5 flex-nowrap overflow-hidden w-fit max-w-full mx-auto',
          compact ? 'hidden' : 'hidden @bp-sm:flex'
        )}
      >
        <button
          type="button"
          className={cx(btnBase, 'gap-1 px-2.5 @bp-sm:min-w-0')}
          disabled={prevDisabled}
          onClick={() => go(safeCurrent - 1)}
          aria-label={t('common.previous', 'Previous')}
        >
          <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
          <span className={cx('whitespace-nowrap', 'text-small')}>
            {t('common.previous', 'Previous')}
          </span>
        </button>

        {pages.map((token, idx) =>
          token === 'ellipsis' ? (
            <span
              key={`e-${idx}`}
              className={cx(
                'inline-flex items-center justify-center min-w-9 min-h-9',
                'text-center text-tertiary select-none pointer-events-none',
                'text-small'
              )}
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={token}
              type="button"
              className={cx(btnBase, token === safeCurrent && btnCurrent)}
              aria-current={token === safeCurrent ? 'page' : undefined}
              onClick={() => go(token)}
            >
              <span className={cx('tabular-nums', 'text-small')}>{token}</span>
            </button>
          )
        )}

        <button
          type="button"
          className={cx(btnBase, 'gap-1 px-2.5 @bp-sm:min-w-0')}
          disabled={nextDisabled}
          onClick={() => go(safeCurrent + 1)}
          aria-label={t('common.next', 'Next')}
        >
          <span className={cx('whitespace-nowrap', 'text-small')}>
            {t('common.next', 'Next')}
          </span>
          <ChevronRight className="w-4 h-4 shrink-0" aria-hidden />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
